import { Fragment, useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

interface AuditRow {
  id: string;
  occurredAt: string;
  actorType: string;
  actorName: string | null;
  actorIp: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  result: 'SUCCESS' | 'DENIED' | 'FAILED';
  denyReason: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  requestId: string | null;
}

/** 动作的中文说法。没收录的直接显示原文,不显示 undefined */
const ACTION_LABEL: Record<string, string> = {
  LOGIN: '登录',
  PERMISSION_DENIED: '权限拒绝',
  ORDER_EXPORT: '导出订单',
  DRIVER_PASSWORD_RESET: '重置司机密码',
  DRIVER_STATUS_CHANGE: '变更司机状态',
};

const RESULT_META: Record<string, { label: string; cls: string }> = {
  SUCCESS: { label: '成功', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  DENIED: { label: '被拒', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' },
  FAILED: { label: '失败', cls: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300' },
};

/**
 * 审计日志。
 *
 * ★ 只读。表在数据库层就是 append-only —— 界面上也就不该有
 *   「删除」「编辑」这类按钮,能改的审计日志不是审计日志。
 *
 * 默认只看**异常**(被拒 + 失败):正常操作的量级远大于异常,
 * 一进来就是几千条成功登录的话,真正要看的东西反而被淹了。
 */
export function AuditLog() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [action, setAction] = useState('');
  const [result, setResult] = useState<string>('ABNORMAL');
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(
    async (append = false) => {
      setLoading(true);
      try {
        const p = new URLSearchParams({ limit: '50' });
        if (action) p.set('action', action);
        // 「异常」不是后端的一个值,是 DENIED + FAILED 两种。
        // 后端按单值筛,所以这里分两次取再合并 —— 量不大,够用
        if (result && result !== 'ABNORMAL') p.set('result', result);
        if (append && cursor) p.set('cursor', cursor);

        if (result === 'ABNORMAL') {
          const [denied, failed] = await Promise.all([
            api<{ data: AuditRow[] }>(`/audit-logs?${p}&result=DENIED`),
            api<{ data: AuditRow[] }>(`/audit-logs?${p}&result=FAILED`),
          ]);
          const merged = [...denied.data, ...failed.data].sort((a, b) =>
            b.occurredAt.localeCompare(a.occurredAt),
          );
          setRows(merged);
          setHasMore(false);
          setCursor(null);
        } else {
          const r = await api<{
            data: AuditRow[];
            meta: { cursor: { next: string | null; hasMore: boolean } };
          }>(`/audit-logs?${p}`);
          setRows((prev) => (append ? [...prev, ...r.data] : r.data));
          setCursor(r.meta.cursor.next);
          setHasMore(r.meta.cursor.hasMore);
        }
        setError(null);
      } catch (e) {
        setError(e instanceof ApiFailure ? e.message : '加载失败');
      } finally {
        setLoading(false);
      }
    },
    [action, result, cursor],
  );

  useEffect(() => {
    setCursor(null);
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, result]);

  useEffect(() => {
    void api<string[]>('/audit-logs/actions').then(setActions).catch(() => {});
  }, []);

  return (
    <div className="p-4 lg:p-6">
      <h1 className="mb-1 text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">审计日志</h1>
      <p className="mb-4 text-sm text-slate-500">
        只读记录,写入后不可修改或删除。默认只显示异常事件。
      </p>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <select
          aria-label="结果"
          value={result}
          onChange={(e) => setResult(e.target.value)}
          className={selectCls}
        >
          <option value="ABNORMAL">只看异常(被拒 / 失败)</option>
          <option value="">全部结果</option>
          <option value="SUCCESS">成功</option>
          <option value="DENIED">被拒</option>
          <option value="FAILED">失败</option>
        </select>

        <select
          aria-label="动作"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className={selectCls}
        >
          <option value="">全部动作</option>
          {actions.map((a) => (
            <option key={a} value={a}>
              {ACTION_LABEL[a] ?? a}
            </option>
          ))}
        </select>

        <button
          onClick={() => void load(false)}
          disabled={loading}
          className="h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                     text-sm text-slate-600 dark:text-slate-400 disabled:opacity-50"
        >
          {loading ? '刷新中…' : '刷新'}
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-2 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                      bg-white dark:bg-slate-800">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
            <tr>
              <Th>时间</Th>
              <Th>操作者</Th>
              <Th>动作</Th>
              <Th>对象</Th>
              <Th>结果</Th>
              <Th>来源 IP</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              // key 要给 Fragment,给里面的 tr 不算 —— React 看的是
              // map 直接返回的那个元素
              <Fragment key={r.id}>
                <tr
                  className="border-t border-slate-100 dark:border-slate-700/50
                             hover:bg-slate-50 dark:hover:bg-slate-700/30"
                >
                  <td className="p-3 whitespace-nowrap text-slate-500 tabular-nums text-xs">
                    {new Date(r.occurredAt).toLocaleString('zh-CN')}
                  </td>
                  <td className="p-3 text-slate-900 dark:text-slate-100">
                    {r.actorName ?? '—'}
                    <div className="text-xs text-slate-400">{r.actorType}</div>
                  </td>
                  <td className="p-3 text-slate-900 dark:text-slate-100">
                    {ACTION_LABEL[r.action] ?? r.action}
                  </td>
                  <td className="p-3 text-slate-600 dark:text-slate-400 max-w-xs truncate">
                    {r.resourceType}
                    {r.resourceId && <span className="text-slate-400"> · {r.resourceId}</span>}
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        RESULT_META[r.result]?.cls ?? ''
                      }`}
                    >
                      {RESULT_META[r.result]?.label ?? r.result}
                    </span>
                    {r.denyReason && (
                      <div className="mt-0.5 text-xs text-slate-500 max-w-xs truncate">
                        {r.denyReason}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-500 font-mono">{r.actorIp ?? '—'}</td>
                  <td className="p-3">
                    <button
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      className="text-xs text-brand-600 dark:text-brand-500"
                    >
                      {expanded === r.id ? '收起' : '详情'}
                    </button>
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr className="bg-slate-50 dark:bg-slate-900/40">
                    <td colSpan={7} className="p-3">
                      <Detail row={r} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="p-10 text-center text-slate-400">
                  {result === 'ABNORMAL' ? '没有异常事件 —— 这是好事' : '没有记录'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button
          onClick={() => void load(true)}
          disabled={loading}
          className="mt-3 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600
                     text-sm text-slate-700 dark:text-slate-300 disabled:opacity-50"
        >
          {loading ? '加载中…' : '加载更多'}
        </button>
      )}
    </div>
  );
}

const selectCls =
  'h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm';

function Th({ children }: { children: React.ReactNode }) {
  return <th className="p-3 text-left font-medium whitespace-nowrap">{children}</th>;
}

function Detail({ row }: { row: AuditRow }) {
  const blocks: Array<[string, unknown]> = [];
  if (row.before) blocks.push(['变更前', row.before]);
  if (row.after) blocks.push(['变更后', row.after]);
  if (Object.keys(row.metadata ?? {}).length > 0) blocks.push(['详情', row.metadata]);
  if (row.requestId) blocks.push(['请求 ID', row.requestId]);

  if (blocks.length === 0) return <span className="text-sm text-slate-400">没有附加信息</span>;

  return (
    <div className="space-y-2">
      {blocks.map(([label, value]) => (
        <div key={label}>
          <div className="text-xs text-slate-500 mb-0.5">{label}</div>
          <pre className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap
                          bg-white dark:bg-slate-800 rounded-lg p-2 border
                          border-slate-200 dark:border-slate-700 overflow-x-auto">
            {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  );
}
